import "react-international-phone/style.css";
import {
    BaseTextFieldProps,
    InputAdornment,
    MenuItem,
    Select,
    TextField,
    Typography,
} from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import {
    CountryIso2,
    defaultCountries,
    FlagImage,
    parseCountry,
    usePhoneInput,
} from "react-international-phone";
import useGeneralStore from "@/store/General/useGeneralStore";

export interface PhoneCodeSelectorProps extends BaseTextFieldProps {
    value: string;
    onChange: (phone: string) => void;
}

export const PhoneCodeSelector: React.FC<PhoneCodeSelectorProps> = ({
    value,
    onChange,
    ...restProps
}) => {
    const {
        inputValue,
        handlePhoneValueChange,
        inputRef,
        country,
        setCountry,
    } = usePhoneInput({
        defaultCountry: "us",
        value,
        countries: defaultCountries,
        onChange: (data) => {
            if (!restProps?.name) {
                onChange(data.phone);
            } else {
                //@ts-ignore
                onChange({
                    target: {
                        name: restProps.name,
                        value: data.phone,
                    },
                });
            }
        },
    });

    const [menuWidth, setMenuWidth] = useState<number>(0);
    const inputContainerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (inputContainerRef.current) {
            setMenuWidth(inputContainerRef.current.offsetWidth);
        }
    }, [inputContainerRef.current]);

    const { PhoneCountries } = useGeneralStore();
    const charCodes = PhoneCountries?.map((country: any) =>
        country.charCode?.toLowerCase()
    );
    const countries = defaultCountries.filter((country) => {
        const { iso2 } = parseCountry(country);
        if (charCodes) return charCodes.includes(iso2);
        return ["kw", "eg", "sa"].includes(iso2);
    });

    return (
        <div
            className="[&_.MuiTypography-root]:ml-auto w-full"
            ref={inputContainerRef}
        >
            <TextField
                className="w-full"
                variant="outlined"
                color="primary"
                size="small"
                placeholder="Phone number"
                value={inputValue}
                onChange={handlePhoneValueChange}
                type="tel"
                inputRef={inputRef}
                InputProps={{
                    startAdornment: (
                        <InputAdornment
                            position="start"
                            className="-ml-[0.6rem] mr-[0.5rem]"
                        >
                            <Select
                                MenuProps={{
                                    PaperProps: {
                                        style: {
                                            width: `${menuWidth}px`,
                                        },
                                    },
                                    anchorOrigin: {
                                        vertical: "bottom",
                                        horizontal: "left",
                                    },
                                    transformOrigin: {
                                        vertical: "top",
                                        horizontal: "left",
                                    },
                                }}
                                sx={{
                                    fieldset: {
                                        display: "none",
                                    },
                                    '&.Mui-focused:has(div[aria-expanded="false"])':
                                        {
                                            fieldset: {
                                                display: "none",
                                            },
                                        },
                                    ".MuiSelect-select": {
                                        padding: "0.5em",
                                        paddingRight: "2em !important",
                                    },
                                    svg: {
                                        right: 0,
                                    },
                                }}
                                value={country.iso2}
                                onChange={(e) =>
                                    setCountry(e.target.value as CountryIso2)
                                }
                                renderValue={(value) => (
                                    <FlagImage
                                        iso2={value}
                                        style={{ display: "flex" }}
                                    />
                                )}
                            >
                                {countries.map((c) => {
                                    const country = parseCountry(c);
                                    return (
                                        <MenuItem
                                            key={country.iso2}
                                            value={country.iso2}
                                            sx={{
                                                display: "flex",
                                                alignItems: "center",
                                            }}
                                        >
                                            <FlagImage
                                                iso2={country.iso2}
                                                className="mr-2"
                                            />
                                            <Typography
                                                sx={{ marginRight: "auto" }}
                                            >
                                                {country.name}
                                            </Typography>
                                            <Typography className="text-gray-500">
                                                +{country.dialCode}
                                            </Typography>
                                        </MenuItem>
                                    );
                                })}
                            </Select>
                        </InputAdornment>
                    ),
                }}
                {...restProps}
            />
        </div>
    );
};
